import type { ShowLockedPeriods } from "@components/accounting/schedules/useScheduleLockedPeriodsDialog";
import { ReactNode, useCallback, useState } from "react";
import { SchedulePeriodLockedError } from "@api/scheduleErrors";
import { ScheduleLifecyclePeriodLockedError } from "@common/schemas/domains/accounting/scheduleLifecycle.schema";
import { ScheduleLockedPeriodsModal } from "@components/accounting/schedules/ScheduleLockedPeriodsModal";
import { useAccruedLockedStartToast } from "./useAccruedLockedStartToast";

/**
 * The locked-period dialog as a local node. The global one is an overlay, and
 * the match drawers and side-panel drawers are overlays too, so opening it
 * there would close the drawer the user is working in.
 */
export function useInlineScheduleLockedPeriods(): {
    showLockedPeriods: ShowLockedPeriods;
    lockedPeriodsModal: ReactNode;
} {
    const [lockedError, setLockedError] =
        useState<ScheduleLifecyclePeriodLockedError | null>(null);
    const [isOpen, setIsOpen] = useState(false);
    const showAccruedLockedStart = useAccruedLockedStartToast();

    const showLockedPeriods = useCallback(
        (error: unknown): boolean => {
            if (showAccruedLockedStart(error)) {
                return true;
            }
            if (!(error instanceof SchedulePeriodLockedError)) {
                return false;
            }
            setLockedError(error.details);
            setIsOpen(true);
            return true;
        },
        [showAccruedLockedStart],
    );

    const lockedPeriodsModal = lockedError ? (
        <ScheduleLockedPeriodsModal
            show={isOpen}
            error={lockedError}
            onHide={() => setIsOpen(false)}
        />
    ) : null;

    return { showLockedPeriods, lockedPeriodsModal };
}
